import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export const usePortalTooltip = (enabled: boolean) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const hovered = useRef(false);
  const focused = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>();

  const close = useCallback(() => {
    setIsOpen(false);
    setPosition(undefined);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current || !bubbleRef.current) return;

    const trigger = triggerRef.current.getBoundingClientRect();
    const bubble = bubbleRef.current.getBoundingClientRect();
    const gap = 8;
    const top =
      trigger.top >= bubble.height + gap * 2
        ? trigger.top - bubble.height - gap
        : Math.max(gap, Math.min(trigger.bottom + gap, window.innerHeight - bubble.height - gap));
    const left = Math.max(gap, Math.min(trigger.right - bubble.width, window.innerWidth - bubble.width - gap));
    setPosition({ top, left });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    // Dismiss on scrolling so the fixed bubble cannot linger over a different row.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, close]);

  const open = () => {
    if (enabled) setIsOpen(true);
  };

  return {
    triggerRef,
    bubbleRef,
    isOpen,
    position,
    onMouseEnter: () => {
      hovered.current = true;
      open();
    },
    onMouseLeave: () => {
      hovered.current = false;
      if (!focused.current) close();
    },
    onFocus: () => {
      focused.current = true;
      open();
    },
    onBlur: () => {
      focused.current = false;
      if (!hovered.current) close();
    },
  };
};
